import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Switch,
  Button,
  Card,
  Row,
  Col,
  Upload,
  TreeSelect,
  message,
  Tooltip,
  Space,
  Typography,
  Avatar,
  Tag,
  Spin,
  InputNumber,
} from 'antd';
import {
  InfoCircleOutlined,
  UploadOutlined,
  SaveOutlined,
  EditOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import axios from "axios";
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';

const { Title, Text } = Typography;
const { Option } = Select;

const regionOptions = ["India", "China", "Global"];
const roleOptions = [
  "admin",
  "unithead",
  "peoplemanager",
  "employee",
  "special",
  "special-PatOnTheBack",
  "allcountries",
  "notVisibleToAllUsers",
];
const approverOptions = [
  "admin",
  "unithead",
  "peoplemanager",
  "employee",
  "special",
  "",
];

type RewardFormInputs = {
  rewardName: string;
  parentCategoryId: string;
  type: string;
  requiresApproval: string;
  isActive: boolean;
  eligibleToNominate: string;
  budget: number | "";
  regionName: string[];
  rolesList: string[];
  approver1: string;
  approver2: string;
  approver3: string;
  approver4: string;
  approver5: string;
  icon: UploadFile[];
};

const fieldHelp: Record<keyof RewardFormInputs, string> = {
  rewardName: "The display name of the reward.",
  parentCategoryId: "Group this reward under a parent category for hierarchy.",
  type: "Choose if this is an individual or team reward.",
  requiresApproval:
    "Does this reward require approval? 'false' = no approval, 'true' = approval needed, 'admin'/'global' = special flows.",
  isActive: "Toggle to enable or disable this reward.",
  eligibleToNominate:
    "'anyone' = all users can nominate. 'reportees' = only people managers for their direct reports.",
  budget: "Points or value associated with this reward.",
  regionName: "Select which regions/countries this reward is available in.",
  rolesList:
    "Only users with these roles can see or nominate for this reward. Use 'notVisibleToAllUsers' for confidential rewards.",
  approver1: "First approver in the workflow.",
  approver2: "Second approver in the workflow (optional).",
  approver3: "Third approver in the workflow (optional).",
  approver4: "Fourth approver in the workflow (optional).",
  approver5: "Fifth approver in the workflow (optional).",
  icon: "Upload an icon/image for this reward.",
};

const FieldLabel: React.FC<{ label: string; tooltip: string }> = ({ label, tooltip }) => (
  <Space>
    <span>{label}</span>
    <Tooltip title={tooltip}>
      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
    </Tooltip>
  </Space>
);

interface TreeNode {
  label: string;
  value: string;
  children?: TreeNode[];
}

export default function UpdateAwards() {
  const [form] = Form.useForm();
  const [rewardList, setRewardList] = useState<any[]>([]);
  const [parentCategoriesTree, setParentCategoriesTree] = useState<TreeNode[]>([]);
  const [selectedReward, setSelectedReward] = useState<string | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchingReward, setFetchingReward] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { instance, accounts } = useMsal();
  
  const requiresApproval = Form.useWatch('requiresApproval', form);
  const formValues = Form.useWatch([], form) || {};

  // Utility to build tree for TreeSelect
  function buildTree(
    items: any[],
    parentId: string | null = null
  ): TreeNode[] {
    return items
      .filter((item) => {
        // Treat empty string, null, undefined, or "null" string as root items
        const itemParentId = !item.parentCategoryId || item.parentCategoryId === "null" 
          ? null 
          : item.parentCategoryId;
        return itemParentId === parentId;
      })
      .map((item) => ({
        label: item.rewardName,
        value: item.rewardTypeId,
        children: buildTree(items, item.rewardTypeId),
      }));
  }

  // Fetch all rewards for dropdown and tree
  useEffect(() => {
    const fetchRewards = async () => {
      try {
        const token = await fetchToken();
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/Rewards`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRewardList(res.data);
        const tree = buildTree(res.data, null);
        setParentCategoriesTree([{ label: "None", value: "" }, ...tree]);
          
      } catch (err) {
        setRewardList([]);
        setParentCategoriesTree([{ label: "None", value: "" }]);
      }
    };
    fetchRewards();
    // eslint-disable-next-line
  }, []);

  // Azure AD token
  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    return res.idToken;
  };

  // When a reward is selected, fetch its details and populate the form
  const handleRewardSelect = async (id: string) => {
    setSelectedReward(id);
    setFetchingReward(true);
    try {
      const token = await fetchToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Rewards/getAward/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rewardData = response.data;
      
      // Normalize parentCategoryId: treat null, undefined, "null", or empty string as ""
      const normalizedParentCategoryId = !rewardData.parentCategoryId || 
                                          rewardData.parentCategoryId === "null" 
                                          ? "" 
                                          : rewardData.parentCategoryId;
      
      form.setFieldsValue({
        rewardName: rewardData.rewardName,
        parentCategoryId: normalizedParentCategoryId,
        type: rewardData.type,
        requiresApproval: rewardData.requiresApproval,
        isActive: rewardData.isActive,
        eligibleToNominate: rewardData.eligibleToNominate,
        budget: rewardData.budget,
        regionName: rewardData.regionName,
        rolesList: rewardData.rolesList,
        approver1: rewardData.approver1,
        approver2: rewardData.approver2,
        approver3: rewardData.approver3,
        approver4: rewardData.approver4,
        approver5: rewardData.approver5,
      });
      setImagePreview(rewardData.icon);
      setIconPreview(null);
      setFileList([]);
    } catch (error) {
      message.error('Failed to fetch reward details.');
    } finally {
      setFetchingReward(false);
    }
  };

  // Form submission handler
  const onSubmit = async (values: any) => {
    setLoading(true);
    const formData = new FormData();
    
    Object.entries(values).forEach(([key, value]) => {
      if (key === "icon" && fileList.length > 0 && fileList[0].originFileObj) {
        formData.append("Icon", fileList[0].originFileObj);
      } else if (Array.isArray(value)) {
        value.forEach((v) => formData.append(key, v));
      } else if (value !== undefined && value !== null) {
        formData.append(key, value as any);
      }
    });

    try {
      const token = await fetchToken();
      await axios.put(`${process.env.REACT_APP_API_URL}/api/Rewards/updateAward/${selectedReward}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      message.success("Reward updated successfully!");
    } catch (err) {
      message.error("Failed to update reward.");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadChange = ({ fileList: newFileList }: any) => {
    setFileList(newFileList);
    if (newFileList.length > 0 && newFileList[0].originFileObj) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setIconPreview(e.target?.result as string);
      };
      reader.readAsDataURL(newFileList[0].originFileObj);
    } else {
      setIconPreview(null);
    }
  };

  const uploadProps = {
    beforeUpload: (file: File) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
      }
      return false; // Prevent auto upload
    },
    maxCount: 1,
    fileList,
    onChange: handleUploadChange,
  };

  return (
    <div style={{ margin: '0px auto', padding: '0 0px', width: '100%' }}>

      {/* Reward Selection */}
      <Card 
        title="Select Reward" 
        bordered={false}
        style={{ marginBottom: 16, boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)' }}
      >
        <Select
          value={selectedReward || undefined}
          onChange={handleRewardSelect}
          placeholder="Select a reward to edit"
          style={{ width: '100%' }}
          size="large"
          showSearch
          optionFilterProp="children"
        >
          {rewardList.map((reward) => (
            <Option key={reward.rewardTypeId} value={reward.rewardTypeId}>
              {reward.rewardName}
            </Option>
          ))}
        </Select>
      </Card>

      {fetchingReward ? (
        <Card bordered={false} style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" tip="Loading reward details..." />
        </Card>
      ) : selectedReward ? (
        <>
          <Form
            form={form}
            layout="vertical"
            onFinish={onSubmit}
            initialValues={{
              type: 'individual',
              requiresApproval: 'false',
              isActive: true,
              eligibleToNominate: 'anyone',
              regionName: [],
              rolesList: [],
              parentCategoryId: '',
            }}
            requiredMark="optional"
          >
            <Card 
              title="Basic Information" 
              bordered={false}
              style={{ marginBottom: 16, boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)' }}
            >
              <Row gutter={[16, 0]}>
                {(iconPreview || imagePreview) && (
                  <Col xs={24}>
                    <Space direction="vertical" size="small">
                      <Text type="secondary">Current Icon:</Text>
                      <Avatar
                        src={iconPreview || (imagePreview?.startsWith('http') || imagePreview?.startsWith('data:image')
                          ? imagePreview
                          : imagePreview
                            ? `data:image/svg+xml;base64,${imagePreview}`
                            : undefined)}
                        size={64}
                        shape="square"
                      />
                    </Space>
                  </Col>
                )}
                
                <Col xs={24} lg={12}>
                  <Form.Item
                    name="rewardName"
                    label={<FieldLabel label="Reward Name" tooltip={fieldHelp.rewardName} />}
                    rules={[{ required: true, message: 'Please enter reward name' }]}
                  >
                    <Input placeholder="Enter reward name" />
                  </Form.Item>
                </Col>
                
                <Col xs={24} lg={12}>
                  <Form.Item
                    name="parentCategoryId"
                    label={<FieldLabel label="Parent Category" tooltip={fieldHelp.parentCategoryId} />}
                  >
                    <TreeSelect
                      treeData={parentCategoriesTree}
                      placeholder="Select a parent award (optional)"
                      allowClear
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    name="type"
                    label={<FieldLabel label="Type" tooltip={fieldHelp.type} />}
                  >
                    <Select placeholder="Select type">
                      <Option value="individual">Individual</Option>
                      <Option value="team">Team</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    name="requiresApproval"
                    label={<FieldLabel label="Requires Approval" tooltip={fieldHelp.requiresApproval} />}
                  >
                    <Select placeholder="Select approval type">
                      <Option value="false">No</Option>
                      <Option value="true">Yes</Option>
                      <Option value="admin">Admin</Option>
                      <Option value="global">Global</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    name="isActive"
                    label={<FieldLabel label="Is Active" tooltip={fieldHelp.isActive} />}
                    valuePropName="checked"
                  >
                    <Switch 
                      checkedChildren="Active" 
                      unCheckedChildren="Inactive"
                      size="default"
                      style={{ width: 100 }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    name="eligibleToNominate"
                    label={<FieldLabel label="Eligible To Nominate" tooltip={fieldHelp.eligibleToNominate} />}
                  >
                    <Select placeholder="Select eligibility">
                      <Option value="anyone">Anyone</Option>
                      <Option value="reportees">People Managers (for their reportees)</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    name="budget"
                    label={<FieldLabel label="Budget" tooltip={fieldHelp.budget} />}
                  >
                    <InputNumber
                      placeholder="Enter budget"
                      min={0}
                      style={{ width: '100%' }}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => value?.replace(/,/g, '') as any}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card 
              title="Configuration" 
              bordered={false}
              style={{ marginBottom: 16, boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)' }}
            >
              <Row gutter={[16, 0]}>
                <Col xs={24} lg={12}>
                  <Form.Item
                    name="regionName"
                    label={<FieldLabel label="Region Name" tooltip={fieldHelp.regionName} />}
                  >
                    <Select
                      mode="tags"
                      placeholder="Select or type regions"
                      tokenSeparators={[',']}
                      options={regionOptions.map(r => ({ label: r, value: r.toLowerCase() }))}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    name="rolesList"
                    label={<FieldLabel label="Roles List" tooltip={fieldHelp.rolesList} />}
                    extra={<Text type="secondary" style={{ fontSize: 12 }}>Use <Text strong>notVisibleToAllUsers</Text> for confidential rewards</Text>}
                  >
                    <Select
                      mode="tags"
                      placeholder="Select or type roles"
                      tokenSeparators={[',']}
                      options={roleOptions.map(r => ({ label: r, value: r }))}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    name="icon"
                    label={<FieldLabel label="Icon" tooltip={fieldHelp.icon} />}
                  >
                    <Upload {...uploadProps} listType="picture">
                      <Button icon={<UploadOutlined />}>
                        Choose File
                      </Button>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {requiresApproval !== 'false' && (
              <Card 
                title="Approval Workflow" 
                bordered={false}
                style={{ marginBottom: 16, boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)' }}
              >
                <Row gutter={[16, 0]}>
                  {[1, 2, 3, 4, 5].map((num) => (
                    <Col xs={24} sm={12} lg={8} key={num}>
                      <Form.Item
                        name={`approver${num}`}
                        label={
                          <FieldLabel 
                            label={`Approver ${num}`} 
                            tooltip={fieldHelp[`approver${num}` as keyof RewardFormInputs]} 
                          />
                        }
                      >
                        <Select placeholder={`Select approver ${num}`} allowClear>
                          {approverOptions.map((role) => (
                            <Option key={role || 'none'} value={role}>
                              {role || 'None'}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              </Card>
            )}

            <Card 
              bordered={false}
              style={{ marginBottom: 16, boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)' }}
            >
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                disabled={!selectedReward}
              >
                Update Reward
              </Button>
            </Card>
          </Form>

          {/* Live Preview */}
          <Card 
            title={
              <Space size="small">
                <EditOutlined />
                <span>Reward Preview</span>
              </Space>
            }
            bordered={false}
            style={{ boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)' }}
          >
            <Row gutter={16} align="middle">
              <Col>
                {iconPreview || imagePreview ? (
                  <Avatar
                    src={
                      iconPreview ||
                      (imagePreview?.startsWith('http') || imagePreview?.startsWith('data:image')
                        ? imagePreview
                        : imagePreview
                          ? `data:image/svg+xml;base64,${imagePreview}`
                          : undefined)
                    }
                    size={64}
                    shape="square"
                  />
                ) : (
                  <Avatar size={64} shape="square" style={{ backgroundColor: '#1890ff' }}>
                    {formValues.rewardName?.[0]?.toUpperCase() || '?'}
                  </Avatar>
                )}
              </Col>
              <Col flex="auto">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Title level={5} style={{ margin: 0 }}>
                    {formValues.rewardName || 'Reward Name'}
                  </Title>
                  
                  <Space wrap size="small">
                    <Tag color="blue">{formValues.type?.toUpperCase() || 'INDIVIDUAL'}</Tag>
                    <Tag color={formValues.isActive ? 'green' : 'red'}>
                      {formValues.isActive ? 'ACTIVE' : 'INACTIVE'}
                    </Tag>
                    {formValues.requiresApproval !== 'false' && (
                      <Tag color="orange">REQUIRES APPROVAL</Tag>
                    )}
                  </Space>

                  <div style={{ marginTop: 8, fontSize: '13px' }}>
                    {formValues.regionName && formValues.regionName.length > 0 && (
                      <div style={{ marginBottom: 2 }}>
                        <Text type="secondary" style={{ fontSize: '13px' }}>Regions: </Text>
                        <Text style={{ fontSize: '13px' }}>{formValues.regionName.join(', ')}</Text>
                      </div>
                    )}
                    
                    {formValues.rolesList && formValues.rolesList.length > 0 && (
                      <div style={{ marginBottom: 2 }}>
                        <Text type="secondary" style={{ fontSize: '13px' }}>Roles: </Text>
                        <Text style={{ fontSize: '13px' }}>{formValues.rolesList.join(', ')}</Text>
                      </div>
                    )}
                    
                    {formValues.budget && (
                      <div style={{ marginBottom: 2 }}>
                        <Text type="secondary" style={{ fontSize: '13px' }}>Budget: </Text>
                        <Text strong style={{ fontSize: '13px' }}>{formValues.budget}</Text>
                      </div>
                    )}

                    <div style={{ marginBottom: 2 }}>
                      <Text type="secondary" style={{ fontSize: '13px' }}>Eligible To Nominate: </Text>
                      <Text style={{ fontSize: '13px' }}>{formValues.eligibleToNominate || 'anyone'}</Text>
                    </div>

                    {requiresApproval !== 'false' && (
                      <div>
                        <Text type="secondary" style={{ fontSize: '13px' }}>Approval Chain: </Text>
                        <Text style={{ fontSize: '13px' }}>
                          {[
                            formValues.approver1,
                            formValues.approver2,
                            formValues.approver3,
                            formValues.approver4,
                            formValues.approver5
                          ]
                            .filter(Boolean)
                            .join(' → ') || 'None configured'}
                        </Text>
                      </div>
                    )}
                  </div>
                </Space>
              </Col>
            </Row>
          </Card>
        </>
      ) : (
        <Card bordered={false} style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="secondary">Please select a reward from the dropdown above to edit</Text>
        </Card>
      )}
    </div>
  );
}